import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
    CreateCategoryInput,
    CreateQuestionsInput,
    MeasurementOption,
    MeasurementType,
    RubricV2,
    UpdateCategoryInput,
    UpdateQuestionInput,
} from 'generated/graphql';
import { RubricData } from 'Interfaces/Rubric/Rubric';
import {
    CategoryColumn,
    Column,
    ToUpdateCategory,
    ToUpdateSubCategory,
} from 'Interfaces/Rubric/RubricComponent/CategoryColumn';
import { RemovedQuestion } from 'Interfaces/Rubric/RubricComponent/RemovedQuestions';
import addRequireCommentToSubCategory from 'Utils/Rubric/addRequireCommentToSubCategory';
import { v4 as uuid } from 'uuid';
import rubricInitialState from './state';

const addRemovedCategory = (
    categoryThatIsAboutToGetRemoved: string,
    currentRemovedCategories: string[] = [],
): string[] => {
    const removedOldCategoriesCopy = [].concat(currentRemovedCategories);
    if (removedOldCategoriesCopy.length > 0) {
        removedOldCategoriesCopy.push(categoryThatIsAboutToGetRemoved);
        return removedOldCategoriesCopy;
    }
    return new Array(categoryThatIsAboutToGetRemoved);
};

const addRemovedQuestion = (
    questionThatIsAboutToGetRemoved: RemovedQuestion,
    currentRemovedQuestions: RemovedQuestion[] = [],
): RemovedQuestion[] => {
    const removedOldQuestionsCopy = [].concat(currentRemovedQuestions);
    if (removedOldQuestionsCopy.length > 0) {
        removedOldQuestionsCopy.push(questionThatIsAboutToGetRemoved);
        return removedOldQuestionsCopy;
    }
    return new Array(questionThatIsAboutToGetRemoved);
};

const rubricSlice = createSlice({
    name: 'rubric',
    initialState: rubricInitialState,
    reducers: {
        SAVE_CURRENT_RUBRIC: (state, action: PayloadAction<RubricData>) => {
            const rubricData = action.payload;
            return {
                ...state,
                rubricData,
            };
        },
        SAVE_RUBRIC_ID: (state, action: PayloadAction<string>) => {
            const rubricId = action.payload;
            return {
                ...state,
                rubricId,
            };
        },
        SAVE_RUBRICS: (state, action: PayloadAction<RubricV2[]>) => {
            const rubrics = action.payload;
            return {
                ...state,
                rubrics,
            };
        },

        CLEAN_CURRENT_RUBRIC: (state) => ({
                ...state,
                rubricData: null,
            }),

        SAVE_COLUMNS_OF_CATEGORIES: (state, action: PayloadAction<CategoryColumn>) => {
            const newColumnsOfCategories = action.payload;
            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    columnsOfCategories: newColumnsOfCategories,
                },
            };
        },

        CLEAR_RUBRIC_COMPONENT_DATA: (state) => ({
                ...state,
                rubricComponents: {
                    columnsOfCategories: {},
                    rubricId: null,
                    showFormToUpdateASubCategory: false,
                    showRubricSettings: true,
                    initialNumberOfCategories: 0,
                    updates: {
                        newChanges: false,
                        updatedCategories: [],
                        newCategories: [],
                        newQuestionsFromOldCategories: [],
                        updatedQuestions: [],
                        removedOldCategories: [],
                        removedOldQuestions: [],
                    },
                },
            }),

        UPDATE_CATEGORY_NAME: (state, action: PayloadAction<ToUpdateCategory>) => {
            const { columnId, newCategoryName } = action.payload;
            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    columnsOfCategories: {
                        ...state.rubricComponents.columnsOfCategories,
                        [columnId]: {
                            ...state.rubricComponents.columnsOfCategories[columnId],
                            category: {
                                ...state.rubricComponents.columnsOfCategories[columnId].category,
                                content: {
                                    ...state.rubricComponents.columnsOfCategories[columnId].category.content,
                                    name: newCategoryName,
                                },
                            },
                        },
                    },
                },
            };
        },

        UPDATE_INCLUDE_QUESTIONS: (state, action: PayloadAction<ToUpdateCategory>) => {
            const { columnId, includeQuestions } = action.payload;
            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    columnsOfCategories: {
                        ...state.rubricComponents.columnsOfCategories,
                        [columnId]: {
                            ...state.rubricComponents.columnsOfCategories[columnId],
                            category: {
                                ...state.rubricComponents.columnsOfCategories[columnId].category,
                                content: {
                                    ...state.rubricComponents.columnsOfCategories[columnId].category.content,
                                    includeQuestions,
                                },
                            },
                        },
                    },
                },
            };
        },

        DELETE_SUBCATEGORY: (state, action: PayloadAction<ToUpdateCategory>) => {
            const { columnId, questionId } = action.payload;
            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    columnsOfCategories: {
                        ...state.rubricComponents.columnsOfCategories,
                        [columnId]: {
                            ...state.rubricComponents.columnsOfCategories[columnId],
                            category: {
                                ...state.rubricComponents.columnsOfCategories[columnId].category,
                                content: {
                                    ...state.rubricComponents.columnsOfCategories[columnId].category.content,
                                    questions: state.rubricComponents.columnsOfCategories[
                                        columnId
                                    ].category.content.questions.filter(
                                        (singleSubCategory) => singleSubCategory.id !== questionId,
                                    ),
                                },
                            },
                        },
                    },
                },
            };
        },

        UPDATE_SUBCATEGORIES: (state, action: PayloadAction<ToUpdateCategory>) => {
            const { newSubCategories, columnId } = action.payload;
            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    columnsOfCategories: {
                        ...state.rubricComponents.columnsOfCategories,
                        [columnId]: {
                            ...state.rubricComponents.columnsOfCategories[columnId],
                            category: {
                                ...state.rubricComponents.columnsOfCategories[columnId].category,
                                content: {
                                    ...state.rubricComponents.columnsOfCategories[columnId].category.content,
                                    questions: newSubCategories,
                                },
                            },
                        },
                    },
                },
            };
        },

        ADD_CATEGORY: (state, action: PayloadAction<Column>) => {
            const newCategory = action.payload;
            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    columnsOfCategories: {
                        ...state.rubricComponents.columnsOfCategories,
                        [uuid()]: {
                            ...newCategory,
                        },
                    },
                },
            };
        },

        UPDATE_CATEGORY_POINTS: (state, action: PayloadAction<ToUpdateCategory>) => {
            const { columnId, categoryPoints } = action.payload;
            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    columnsOfCategories: {
                        ...state.rubricComponents.columnsOfCategories,
                        [columnId]: {
                            ...state.rubricComponents.columnsOfCategories[columnId],
                            category: {
                                ...state.rubricComponents.columnsOfCategories[columnId].category,
                                content: {
                                    ...state.rubricComponents.columnsOfCategories[columnId].category.content,
                                    weight: categoryPoints,
                                },
                            },
                        },
                    },
                },
            };
        },

        ENABLE_SHOW_RUBRIC_SETTINGS: (state) => ({
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    showRubricSettings: true,
                },
            }),

        DISABLE_SHOW_RUBRIC_SETTINGS: (state) => ({
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    showRubricSettings: false,
                },
            }),

        UPDATE_PROCESSED_RUBRIC_ID: (state, action) => {
            const newRubricId = action.payload as string;
            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    rubricId: newRubricId,
                },
            };
        },

        SAVE_RANGE_MEASUREMENT_TYPE: (state, action: PayloadAction<ToUpdateCategory>) => {
            const { columnId, requiresComments, autofailEnabled } = action.payload;
            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    columnsOfCategories: {
                        ...state.rubricComponents.columnsOfCategories,
                        [columnId]: {
                            ...state.rubricComponents.columnsOfCategories[columnId],
                            category: {
                                ...state.rubricComponents.columnsOfCategories[columnId].category,
                                content: {
                                    ...state.rubricComponents.columnsOfCategories[columnId].category.content,
                                    measurementType: MeasurementType.Range,
                                    requiresComments,
                                    autofailEnabled,
                                    questions: addRequireCommentToSubCategory(
                                        state.rubricComponents.columnsOfCategories[columnId].category.content.questions,
                                        requiresComments,
                                    ),
                                },
                            },
                        },
                    },
                },
            };
        },
        SAVE_RUBRICS_ID: (state, action: PayloadAction<string[]>) => {
            const newRubricsId = action.payload;
            window.localStorage.setItem('selectedRubricIds', JSON.stringify(newRubricsId));
            return {
                ...state,
                rubricsId: newRubricsId,
            };
        },
        UPDATE_INITIAL_NUMBER_OF_CATEGORIES: (state, action) => {
            const newNumberOfCategories = action.payload as number;
            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    initialNumberOfCategories: newNumberOfCategories,
                },
            };
        },
        UPDATES_SAVE_NEW_CATEGORIES: (state, action: PayloadAction<CreateCategoryInput[]>) => {
            const newCategories = action.payload;
            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    updates: {
                        ...state.rubricComponents.updates,
                        newCategories,
                    },
                },
            };
        },
        SAVE_UPDATES_FOR_CATEGORIES: (state, action: PayloadAction<UpdateCategoryInput[]>) => {
            const updateCategoriesWeight = action.payload;
            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    updates: {
                        ...state.rubricComponents.updates,
                        updatedCategories: updateCategoriesWeight,
                    },
                },
            };
        },
        UPDATES_SAVE_NEW_QUESTIONS_FROM_OLD_CATEGORIES: (state, action: PayloadAction<CreateQuestionsInput[]>) => {
            const newQuestionsFromOldCategories = action.payload;
            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    updates: {
                        ...state.rubricComponents.updates,
                        newQuestionsFromOldCategories,
                    },
                },
            };
        },
        SAVE_UPDATES_FOR_QUESTIONS: (state, action: PayloadAction<UpdateQuestionInput[]>) => {
            const updatedQuestions = action.payload;
            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    updates: {
                        ...state.rubricComponents.updates,
                        updatedQuestions,
                    },
                },
            };
        },
        UPDATES_SAVE_REMOVED_CATEGORY: (state, action: PayloadAction<string>) => {
            const removedCategory = action.payload;
            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    updates: {
                        ...state.rubricComponents.updates,
                        removedOldCategories: addRemovedCategory(
                            removedCategory,
                            state.rubricComponents.updates.removedOldCategories || [],
                        ),
                    },
                },
            };
        },
        CLEAR_RUBRIC_UPDATES: (state) => ({
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    updates: {
                        ...state.rubricComponents.updates,
                        newChanges: false,
                        updatedCategories: [],
                        newCategories: [],
                        newQuestionsFromOldCategories: [],
                        updatedQuestions: [],
                    },
                },
            }),
        UPDATES_SAVE_REMOVED_QUESTION: (state, action: PayloadAction<RemovedQuestion>) => {
            const removedCategory = action.payload;
            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    updates: {
                        ...state.rubricComponents.updates,
                        removedOldQuestions: addRemovedQuestion(
                            removedCategory,
                            state.rubricComponents.updates.removedOldQuestions || [],
                        ),
                    },
                },
            };
        },
        UPDATES_SAVE_NEW_CHANGES_VARIABLE: (state, action: PayloadAction<boolean>) => {
            const newChanges = action.payload;
            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    updates: {
                        ...state.rubricComponents.updates,
                        newChanges,
                    },
                },
            };
        },
        UPDATE_MEASUREMENT_TYPE: (
            state,
            action: PayloadAction<{ columnId: string; measurementType: MeasurementType }>,
        ) => {
            const { columnId, measurementType } = action.payload;

            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    columnsOfCategories: {
                        ...state.rubricComponents.columnsOfCategories,
                        [columnId]: {
                            ...state.rubricComponents.columnsOfCategories[columnId],
                            category: {
                                ...state.rubricComponents.columnsOfCategories[columnId].category,
                                content: {
                                    ...state.rubricComponents.columnsOfCategories[columnId].category.content,
                                    measurementType,
                                },
                            },
                        },
                    },
                },
            };
        },
        UPDATE_MEASUREMENT_OPTIONS: (
            state,
            action: PayloadAction<{ columnId: string; measurementOptions: MeasurementOption[] }>,
        ) => {
            const { columnId, measurementOptions } = action.payload;

            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    columnsOfCategories: {
                        ...state.rubricComponents.columnsOfCategories,
                        [columnId]: {
                            ...state.rubricComponents.columnsOfCategories[columnId],
                            category: {
                                ...state.rubricComponents.columnsOfCategories[columnId].category,
                                content: {
                                    ...state.rubricComponents.columnsOfCategories[columnId].category.content,
                                    measurementOptions,
                                },
                            },
                        },
                    },
                },
            };
        },
        UPDATE_REQUIRE_COMMENTS: (state, action: PayloadAction<{ columnId: string; requiresComments: boolean }>) => {
            const { columnId, requiresComments } = action.payload;

            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    columnsOfCategories: {
                        ...state.rubricComponents.columnsOfCategories,
                        [columnId]: {
                            ...state.rubricComponents.columnsOfCategories[columnId],
                            category: {
                                ...state.rubricComponents.columnsOfCategories[columnId].category,
                                content: {
                                    ...state.rubricComponents.columnsOfCategories[columnId].category.content,
                                    requiresComments,
                                    questions: addRequireCommentToSubCategory(
                                        state.rubricComponents.columnsOfCategories[columnId].category.content.questions,
                                        requiresComments,
                                    ),
                                },
                            },
                        },
                    },
                },
            };
        },
        UPDATE_NOT_APPLICABLE: (state, action: PayloadAction<{ columnId: string; notApplicable: boolean }>) => {
            const { columnId, notApplicable } = action.payload;

            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    columnsOfCategories: {
                        ...state.rubricComponents.columnsOfCategories,
                        [columnId]: {
                            ...state.rubricComponents.columnsOfCategories[columnId],
                            category: {
                                ...state.rubricComponents.columnsOfCategories[columnId].category,
                                content: {
                                    ...state.rubricComponents.columnsOfCategories[columnId].category.content,
                                    notApplicable,
                                },
                            },
                        },
                    },
                },
            };
        },
        UPDATE_AUTO_FAIL_ENABLED: (state, action: PayloadAction<{ columnId: string; autofailEnabled: boolean }>) => {
            const { columnId, autofailEnabled } = action.payload;

            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    columnsOfCategories: {
                        ...state.rubricComponents.columnsOfCategories,
                        [columnId]: {
                            ...state.rubricComponents.columnsOfCategories[columnId],
                            category: {
                                ...state.rubricComponents.columnsOfCategories[columnId].category,
                                content: {
                                    ...state.rubricComponents.columnsOfCategories[columnId].category.content,
                                    autofailEnabled,
                                },
                            },
                        },
                    },
                },
            };
        },
        UPDATE_SUBCATEGORY_WEIGHT: (state, action: PayloadAction<ToUpdateSubCategory>) => {
            const { columnId, indexOfCurrentSubCategory, newWeight } = action.payload;
            return {
                ...state,
                rubricComponents: {
                    ...state.rubricComponents,
                    columnsOfCategories: {
                        ...state.rubricComponents.columnsOfCategories,
                        [columnId]: {
                            ...state.rubricComponents.columnsOfCategories[columnId],
                            category: {
                                ...state.rubricComponents.columnsOfCategories[columnId].category,
                                content: {
                                    ...state.rubricComponents.columnsOfCategories[columnId].category.content,
                                    questions: state.rubricComponents.columnsOfCategories[
                                        columnId
                                    ].category.content.questions.map((content, i) =>
                                        i === indexOfCurrentSubCategory
                                            ? { ...content, weight: newWeight }
                                            : content,
                                    ),
                                },
                            },
                        },
                    },
                },
            };
        },
    },
});

export const { reducer: rubricReducer, actions: rubricActions } = rubricSlice;
